export function memoizeAll1<S1 extends object, T>(f: (arg1: S1) => T): (arg1: S1) => T {
  const results = new WeakMap<S1, T>();

  return (arg1: S1): T => {
    const prevResult = results.get(arg1);
    if (prevResult !== undefined) {
      return prevResult;
    } else {
      const result = f(arg1);
      results.set(arg1, result);
      return result;
    }
  };
}

export function memoizeAll2<S1 extends object, S2 extends object, T>(
  f: (arg1: S1, arg2: S2) => T
): (arg1: S1, arg2: S2) => T {
  const results = new WeakMap<S1, WeakMap<S2, T>>();

  return (arg1: S1, arg2: S2): T => {
    const prevResult1 = results.get(arg1);
    if (prevResult1 !== undefined) {
      const prevResult2 = prevResult1.get(arg2);
      if (prevResult2 !== undefined) {
        return prevResult2;
      } else {
        const result = f(arg1, arg2);
        prevResult1.set(arg2, result);
        return result;
      }
    } else {
      const result = f(arg1, arg2);
      const inner = new WeakMap<S2, T>();
      inner.set(arg2, result);
      results.set(arg1, inner);
      return result;
    }
  };
}

export function memoizeAll3<S1 extends object, S2 extends object, S3 extends object, T>(
  f: (arg1: S1, arg2: S2, arg3: S3) => T
): (arg1: S1, arg2: S2, arg3: S3) => T {
  const results = new WeakMap<S1, WeakMap<S2, WeakMap<S3, T>>>();

  return (arg1: S1, arg2: S2, arg3: S3): T => {
    const prevResult1 = results.get(arg1);
    if (prevResult1 !== undefined) {
      const prevResult2 = prevResult1.get(arg2);
      if (prevResult2 !== undefined) {
        const prevResult3 = prevResult2.get(arg3);
        if (prevResult3 !== undefined) {
          return prevResult3;
        } else {
          const result = f(arg1, arg2, arg3);
          prevResult2.set(arg3, result);
          return result;
        }
      } else {
        const result = f(arg1, arg2, arg3);
        const inner = new WeakMap<S3, T>();
        inner.set(arg3, result);
        prevResult1.set(arg2, inner);
        return result;
      }
    } else {
      const result = f(arg1, arg2, arg3);
      const inner1 = new WeakMap<S3, T>();
      inner1.set(arg3, result);
      const inner2 = new WeakMap<S2, WeakMap<S3, T>>();
      inner2.set(arg2, inner1);
      results.set(arg1, inner2);
      return result;
    }
  };
}

export function memoizeAll4<
  S1 extends object,
  S2 extends object,
  S3 extends object,
  S4 extends object,
  T
>(f: (arg1: S1, arg2: S2, arg3: S3, arg4: S4) => T): (arg1: S1, arg2: S2, arg3: S3, arg4: S4) => T {
  const results = new WeakMap<S1, WeakMap<S2, WeakMap<S3, WeakMap<S4, T>>>>();

  return (arg1: S1, arg2: S2, arg3: S3, arg4: S4): T => {
    const prevResult1 = results.get(arg1);
    if (prevResult1 !== undefined) {
      const prevResult2 = prevResult1.get(arg2);
      if (prevResult2 !== undefined) {
        const prevResult3 = prevResult2.get(arg3);
        if (prevResult3 !== undefined) {
          const prevResult4 = prevResult3.get(arg4);
          if (prevResult4 !== undefined) {
            return prevResult4;
          } else {
            const result = f(arg1, arg2, arg3, arg4);
            prevResult3.set(arg4, result);
            return result;
          }
        } else {
          const result = f(arg1, arg2, arg3, arg4);
          const inner = new WeakMap<S4, T>();
          inner.set(arg4, result);
          prevResult2.set(arg3, inner);
          return result;
        }
      } else {
        const result = f(arg1, arg2, arg3, arg4);
        const inner1 = new WeakMap<S4, T>();
        inner1.set(arg4, result);
        const inner2 = new WeakMap<S3, WeakMap<S4, T>>();
        inner2.set(arg3, inner1);
        prevResult1.set(arg2, inner2);
        return result;
      }
    } else {
      const result = f(arg1, arg2, arg3, arg4);
      const inner1 = new WeakMap<S4, T>();
      inner1.set(arg4, result);
      const inner2 = new WeakMap<S3, WeakMap<S4, T>>();
      inner2.set(arg3, inner1);
      const inner3 = new WeakMap<S2, WeakMap<S3, WeakMap<S4, T>>>();
      inner3.set(arg2, inner2);
      results.set(arg1, inner3);
      return result;
    }
  };
}

export function memoizeAll5<
  S1 extends object,
  S2 extends object,
  S3 extends object,
  S4 extends object,
  S5 extends object,
  T
>(
  f: (arg1: S1, arg2: S2, arg3: S3, arg4: S4, arg5: S5) => T
): (arg1: S1, arg2: S2, arg3: S3, arg4: S4, arg5: S5) => T {
  const results = new WeakMap<S1, WeakMap<S2, WeakMap<S3, WeakMap<S4, WeakMap<S5, T>>>>>();

  return (arg1: S1, arg2: S2, arg3: S3, arg4: S4, arg5: S5): T => {
    const prevResult1 = results.get(arg1);
    if (prevResult1 !== undefined) {
      const prevResult2 = prevResult1.get(arg2);
      if (prevResult2 !== undefined) {
        const prevResult3 = prevResult2.get(arg3);
        if (prevResult3 !== undefined) {
          const prevResult4 = prevResult3.get(arg4);
          if (prevResult4 !== undefined) {
            const prevResult5 = prevResult4.get(arg5);
            if (prevResult5 !== undefined) {
              return prevResult5;
            } else {
              const result = f(arg1, arg2, arg3, arg4, arg5);
              prevResult4.set(arg5, result);
              return result;
            }
          } else {
            const result = f(arg1, arg2, arg3, arg4, arg5);
            const inner = new WeakMap<S5, T>();
            inner.set(arg5, result);
            prevResult3.set(arg4, inner);
            return result;
          }
        } else {
          const result = f(arg1, arg2, arg3, arg4, arg5);
          const inner1 = new WeakMap<S5, T>();
          inner1.set(arg5, result);
          const inner2 = new WeakMap<S4, WeakMap<S5, T>>();
          inner2.set(arg4, inner1);
          prevResult2.set(arg3, inner2);
          return result;
        }
      } else {
        const result = f(arg1, arg2, arg3, arg4, arg5);
        const inner1 = new WeakMap<S5, T>();
        inner1.set(arg5, result);
        const inner2 = new WeakMap<S4, WeakMap<S5, T>>();
        inner2.set(arg4, inner1);
        const inner3 = new WeakMap<S3, WeakMap<S4, WeakMap<S5, T>>>();
        inner3.set(arg3, inner2);
        prevResult1.set(arg2, inner3);
        return result;
      }
    } else {
      const result = f(arg1, arg2, arg3, arg4, arg5);
      const inner1 = new WeakMap<S5, T>();
      inner1.set(arg5, result);
      const inner2 = new WeakMap<S4, WeakMap<S5, T>>();
      inner2.set(arg4, inner1);
      const inner3 = new WeakMap<S3, WeakMap<S4, WeakMap<S5, T>>>();
      inner3.set(arg3, inner2);
      const inner4 = new WeakMap<S2, WeakMap<S3, WeakMap<S4, WeakMap<S5, T>>>>();
      inner4.set(arg2, inner3);
      results.set(arg1, inner4);
      return result;
    }
  };
}
